import { useEffect, useRef, useState } from 'react';

import { useUiMessages } from '../../messages';
import { Icon } from '../Icon';

import { collectDiagnostics } from './collectDiagnostics';
import styles from './VersionOverlay.module.css';

/** One `label: value` pair, e.g. `{ label: 'Admin', value: '1.0.0' }`. */
export interface VersionEntry {
  /** Already-translated label. The package holds no i18n dependency — the app translates. */
  label: string;
  value: string;
}

export interface VersionOverlayProps {
  /** Rendered in order, joined by `separator`. Entries with an empty value are dropped. */
  entries: readonly VersionEntry[];
  /** Glue between entries. */
  separator?: string;
  /**
   * Adds a copy button that writes the entries plus a browser diagnostics report to the
   * clipboard (HS-1222), for pasting into a bug ticket.
   */
  copyable?: boolean;
  /**
   * Extra `label: value` lines for the copied report, placed after the entries. For facts the
   * app knows and the package cannot — the API origin it was built against, the build mode.
   * Technical labels, not translated: see `collectDiagnostics.ts`.
   */
  diagnosticsExtra?: Readonly<Record<string, string>>;
  /**
   * Called on every click of the copy button, before the clipboard is asked — for whatever an
   * app wants to hang on that click besides the copy. The copy itself is unaffected.
   */
  onCopy?: () => void;
  className?: string;
}

const DEFAULT_SEPARATOR = '|';

/** How long the copy confirmation stays up. Matches admin's `CopyField`. */
const COPIED_FEEDBACK_MS = 1500;

/**
 * Build identifier pinned to the bottom-right corner of the viewport (HS-1208), so a tester
 * or support engineer can name the exact build and backend without opening devtools.
 *
 * The fixed box takes no pointer events, so it never intercepts a click on whatever it covers;
 * its text and its copy button opt back in individually (HS-1222 — `pointer-events: none` also
 * suppresses text selection, which is what QA reported).
 *
 * It announces nothing on its own — it is reference text, not a status update. The one exception
 * is the copy confirmation, which is announced politely: a confirmation nobody can perceive is
 * not a confirmation.
 *
 * @interim Ported from the reference design; replaceable by the UI Kit without API breakage.
 */
export function VersionOverlay({
  entries,
  separator = DEFAULT_SEPARATOR,
  copyable = false,
  diagnosticsExtra,
  onCopy,
  className,
}: VersionOverlayProps) {
  const messages = useUiMessages();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const shown = entries.filter((entry) => entry.value !== '');
  if (shown.length === 0) return null;

  const classes = [styles.overlay, className].filter(Boolean).join(' ');

  // The DOM types declare `clipboard` as always present; it is absent outside a secure context
  // (HS-840). Rather than render a button that silently does nothing there, drop the control.
  const clipboard = navigator.clipboard as Clipboard | undefined;
  const showCopy = copyable && clipboard !== undefined;

  const copyLabel = messages.versionOverlay?.copy ?? 'Copy version and browser details';
  const copiedLabel = messages.versionOverlay?.copied ?? 'Copied';

  const copy = () => {
    onCopy?.();
    const report = [
      ...shown.map((entry) => `${entry.label}: ${entry.value}`),
      ...Object.entries(diagnosticsExtra ?? {}).map(([label, value]) => `${label}: ${value}`),
      ...collectDiagnostics(window, new Date()),
    ].join('\n');

    // Only confirm once the write actually resolved — a rejected permission must not report
    // success. A rejection leaves the button idle; the browser has already told the user why.
    void clipboard?.writeText(report).then(
      () => {
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setCopied(false);
        }, COPIED_FEEDBACK_MS);
      },
      () => {
        /* clipboard unavailable or denied — stay idle */
      },
    );
  };

  return (
    <div className={classes}>
      {shown.map((entry, index) => (
        <span key={entry.label} className={styles.entry}>
          {index > 0 && (
            <span className={styles.separator} aria-hidden="true">
              {separator}
            </span>
          )}
          <span className={styles.label}>{entry.label}:</span>
          <span className={styles.value}>{entry.value}</span>
        </span>
      ))}
      {showCopy && (
        <>
          <button
            type="button"
            className={styles.copy}
            aria-label={copied ? copiedLabel : copyLabel}
            onClick={copy}
          >
            <Icon name={copied ? 'check' : 'copy-01'} size={12} />
          </button>
          <span role="status" aria-live="polite" className={styles.srOnly}>
            {copied ? copiedLabel : ''}
          </span>
        </>
      )}
    </div>
  );
}
